import { useEffect, useLayoutEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { post } from '../api';
import { applyBusinessTheme } from '../businessTheme';
import { setPageTitle, useSlugBrand } from '../components/Brand';
import { describe } from '../errors';
import { rememberBusiness } from '../lastBusiness';
import { useSite } from '../site';

/**
 * A business's own sign-in page: the office's `/login/:slug`, or the contractor
 * portal's `/portal/login/:slug`. Teknika CRM's generic sign-in is only the
 * fallback for a link that somehow lacks the business.
 */
export function businessSignIn(business: string, portal: boolean) {
  const slug = business.trim();
  if (!slug) return portal ? '/portal' : '/';
  return `${portal ? '/portal' : ''}/login/${encodeURIComponent(slug)}`;
}

/** Where the emailed invite and reset links land: choose a password, then sign in. */
export function SetPassword(props: { invite: boolean }) {
  const [params] = useSearchParams();
  const business = params.get('business') ?? '';
  // Kept in state so it survives the address being cleaned up below.
  const [token] = useState(() => params.get('token') ?? '');
  const portal = params.get('for') === 'portal';
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  // The emailed link names the business, so this page already wears its colours and name.
  const brand = useSlugBrand(business || undefined);
  useEffect(() => setPageTitle(brand?.name), [brand]);
  useLayoutEffect(() => {
    applyBusinessTheme(business);
    return () => applyBusinessTheme('');
  }, [business]);

  // Take the one-time token out of the address bar so it stays out of history and screenshots.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('token')) return;
    url.searchParams.delete('token');
    window.history.replaceState(window.history.state, '', url);
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== again) {
      setError('The two passwords do not match.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await post('/api/auth/password/set', { business, token, password });
      // So every later sign-in on this device (after signing out, too) is this business's own.
      rememberBusiness(business);
      setDone(true);
    } catch (err) {
      setError(describe(err).problems[0] ?? 'Something went wrong.');
      setBusy(false);
    }
  }

  if (done) {
    // Back to this business's own sign-in (office or portal), not Teknika CRM's generic one.
    const signIn = businessSignIn(business, portal);
    return (
      <div className="card login">
        <h1>Password saved</h1>
        <p>You can now sign in.</p>
        <a className="button" href={signIn}>
          Go to sign in
        </a>
      </div>
    );
  }

  return (
    <form className="card login" onSubmit={submit}>
      <h1>{props.invite ? 'Welcome — choose a password' : 'Choose a new password'}</h1>
      <label>
        New password (at least 10 characters)
        <input
          type="password"
          autoComplete="new-password"
          minLength={10}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </label>
      <label>
        Type it again
        <input
          type="password"
          autoComplete="new-password"
          value={again}
          onChange={(e) => setAgain(e.target.value)}
          required
        />
      </label>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <button type="submit" disabled={busy || token === ''}>
        {busy ? 'Saving…' : 'Save password'}
      </button>
    </form>
  );
}

/** Ask for a reset link. The answer never says whether the email has an account. */
export function ForgotPassword(props: { portal?: boolean }) {
  const [params] = useSearchParams();
  // Reached from a business's own sign-in page, which passes its slug along.
  const from = params.get('business') ?? '';
  const back = from ? businessSignIn(from, !!props.portal) : props.portal ? '/portal' : '/';
  const [business, setBusiness] = useState(from);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const site = useSite();

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await post('/api/auth/password-reset/request', {
        business: site?.slug || business.trim(),
        email,
      });
    } catch {
      // The same message either way.
    }
    setSent(true);
  }

  // Wait for the site rather than flash the business field on a business's own domain.
  if (site === undefined) return null;
  if (sent) {
    return (
      <div className="card login">
        <h1>Check your email</h1>
        <p>If that email has an account, a reset link is on its way.</p>
        <a href={back}>Back to sign in</a>
      </div>
    );
  }
  return (
    <form className="card login" onSubmit={submit}>
      <h1>Reset your password</h1>
      {!site.slug && (
        <label>
          Business
          <input value={business} onChange={(e) => setBusiness(e.target.value)} required />
        </label>
      )}
      <label>
        Email
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </label>
      <button type="submit">Send reset link</button>
      <a href={back}>Back to sign in</a>
    </form>
  );
}
